<template>
  <!-- the dynamic mb-32, class should normally be on the parent, but we don't have the info there -->
  <div
    class="flex flex-col space-y-12"
    :class="{ 'mb-32': bannersToDisplay.length }"
  >
    <TheAutonomousBannerContent
      v-for="banner in bannersToDisplay"
      :key="banner.content"
      :banner
    />
  </div>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { ref, watch } from 'vue'
import type { RouteRecordName } from 'vue-router'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'

import type {
  BannerPageName,
  GetBackOfficeBannersResponse,
} from '~/api/banners/types'

import TheAutonomousBannerContent from './TheAutonomousBannerContent.vue'

const $httpFetch = useHttpFetch()

const props = defineProps<{
  /**
   * An autonomous banner can be put in two type of page:
   *  - page at route level
   *  - page at tab level (nested route)
   * A page name at route level will have a name like: 'options'
   * A page name at tab level will have a name like: 'options.shipping'
   * There is only two possible level of nesting.
   * Since vue router will always return the last child route in case of nested views,
   * we need to know on which level the banner will be displayed.
   * To do so we get back a prop that determines the level we are looking for, and then, based on
   * the meta autonomousBannerPageName attribute that define the page name in the autonomous banner system,
   * call the API with the good option.
   */
  pageLevel: 'ROUTE' | 'TAB'

  /**
   * When `bannerPageName` props is provided, the behavior is very straight forward:
   * A single http call is perform and won't perform again.
   *
   * When `bannerPageName` props is not provided, the `bannerPageName` is retreived from the route definition meta,
   * route.name change are watch and http calls are trigger on every change.
   */
  bannerPageName?: BannerPageName
}>()

const route = useRoute()

const bannersToDisplay = ref<GetBackOfficeBannersResponse>([])

async function fetchBannerToDisplay(pageName: BannerPageName) {
  try {
    const banners = await $httpFetch('/bm/merchants/banners', {
      query: {
        pages: [pageName],
      },
    })

    bannersToDisplay.value = banners.filter((banner) =>
      banner.pages.includes(pageName),
    )
  } catch {
    // fails silently
  }
}

async function initAutonomousBanner(
  newName?: RouteRecordName | null,
  previousName?: RouteRecordName | null,
) {
  // Only perform the action if the route name has actually change
  // We don't want to perform the action when only queryParams change.
  if (newName !== previousName) {
    bannersToDisplay.value = []

    if (route.meta.autonomousBannerPageName) {
      const pageName =
        props.pageLevel === 'ROUTE'
          ? route.meta.autonomousBannerPageName.split('.')[0]
          : route.meta.autonomousBannerPageName

      fetchBannerToDisplay(pageName as BannerPageName)
    }
  }
}

if (!props.bannerPageName) {
  watch(() => route.name, initAutonomousBanner, { deep: true, immediate: true })
}

if (props.bannerPageName) {
  fetchBannerToDisplay(props.bannerPageName)
}
</script>
